import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Identifier } from 'react-admin';

import { setupEcho } from 'providers/sockets/laravel-echo';
import {
  Unit,
  UnitChangedEvent,
  UnitChanges,
  UnitManifest,
  UnitOnlineChanges,
  UnitSoftwareComponentState,
  UpdateState,
} from 'types';
import type Echo from 'laravel-echo';

/**
 * Live unit updates (TVR-2309, TVR-2317).
 *
 * While a unit show page is open, toverzicht-api pushes `.unit.changed` deltas on the private
 * channel `unit-status.{unitId}`. Each delta carries only the fields that changed, in the V3
 * UnitResource format, and is merged straight into react-admin's getOne cache entry, so the page
 * updates without a request. The only request this hook makes is one resync refetch each time the
 * channel subscription is (re)confirmed, which covers whatever was broadcast while not subscribed.
 * Deltas that arrive while any refetch of the record is in flight, that one or one react-admin
 * started itself, are buffered and replayed on top of it once it settles.
 */

/** Event name as Echo expects it: the leading dot means "no namespace prefix" (`broadcastAs`). */
export const UNIT_CHANGED_EVENT = '.unit.changed';

/** The private channel a unit's live updates are broadcast on. */
export const unitStatusChannelName = (unitId: Identifier): string => `unit-status.${unitId}`;

/**
 * An `occurred_at` reduced to a pair that keeps microsecond order. `Date.parse` alone truncates
 * to milliseconds, so the whole seconds and the fraction are kept apart.
 */
export interface OccurredAt {
  /** Whole seconds since the epoch, in milliseconds. */
  epochMs: number;
  /** The fractional second, in nanoseconds. */
  nanos: number;
}

const OCCURRED_AT_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Parses an ISO-8601 timestamp such as `2026-10-05T12:00:00.123456Z`. Returns `null` for anything
 * else, so an event that cannot be ordered is never applied.
 */
export const parseOccurredAt = (value: string): OccurredAt | null => {
  const match = OCCURRED_AT_PATTERN.exec(value);
  if (!match) return null;

  const [, wholeSeconds, fraction = '', zone] = match;
  const epochMs = Date.parse(`${wholeSeconds}${zone}`);
  if (Number.isNaN(epochMs)) return null;

  return { epochMs, nanos: Number(fraction.padEnd(9, '0')) };
};

/** Negative when `a` is older than `b`, zero when they are the same instant, positive when newer. */
export const compareOccurredAt = (a: OccurredAt, b: OccurredAt): number =>
  a.epochMs !== b.epochMs ? a.epochMs - b.epochMs : a.nanos - b.nanos;

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

const ONLINE_STATUSES: ReadonlyArray<Unit['online_status']> = ['online', 'offline', 'realtime'];

const isOnlineStatus = (value: unknown): value is Unit['online_status'] =>
  ONLINE_STATUSES.some((status) => status === value);

// Any finite number is accepted, not only today's 0..3: a state the API adds later must not make
// the live path drop a component or manifest delta that the refetch path would render.
const isUpdateState = (value: unknown): value is UpdateState =>
  isObject(value) && typeof value.value === 'number' && Number.isFinite(value.value) && typeof value.label === 'string';

const isNullableUpdateState = (value: unknown): value is UpdateState | null => value === null || isUpdateState(value);

const toSoftwareComponent = (value: unknown): UnitSoftwareComponentState | null => {
  if (!isObject(value)) return null;

  const { name, version, updating_to: updatingTo, update_state: updateState } = value;
  if (
    typeof name !== 'string' ||
    !isNullableString(version) ||
    !isNullableString(updatingTo) ||
    !isNullableUpdateState(updateState)
  ) {
    return null;
  }

  return { name, version, updating_to: updatingTo, update_state: updateState };
};

const MANIFEST_STRING_FIELDS = [
  'current_release_name',
  'new_release_name',
  'update_channel',
  'last_update_check_at',
  'last_update_received_at',
] as const;

/**
 * Narrows a raw `changes` object to the fields this client knows, each with a value of the right
 * type. Unknown keys, and known keys with a malformed value, are dropped.
 */
const toUnitChanges = (raw: JsonObject): UnitChanges => {
  const changes: UnitChanges = {};

  if (isOnlineStatus(raw.online_status)) changes.online_status = raw.online_status;
  if (typeof raw.is_online === 'boolean') changes.is_online = raw.is_online;
  if (isNullableString(raw.last_online)) changes.last_online = raw.last_online;
  if (typeof raw.is_mqtt_connected === 'boolean') changes.is_mqtt_connected = raw.is_mqtt_connected;

  if (isObject(raw.software_versions)) {
    const versions: NonNullable<Unit['software_versions']> = {};
    for (const [key, version] of Object.entries(raw.software_versions)) {
      if (isNullableString(version)) versions[key] = version;
    }
    if (Object.keys(versions).length > 0) changes.software_versions = versions;
  }

  if (Array.isArray(raw.software_components)) {
    const components = raw.software_components
      .map(toSoftwareComponent)
      .filter((component): component is UnitSoftwareComponentState => component !== null);
    if (components.length > 0) changes.software_components = components;
  }

  if (isObject(raw.manifest)) {
    const manifest: Partial<UnitManifest> = {};
    for (const field of MANIFEST_STRING_FIELDS) {
      const fieldValue = raw.manifest[field];
      if (isNullableString(fieldValue)) manifest[field] = fieldValue;
    }
    if (isNullableUpdateState(raw.manifest.update_state)) manifest.update_state = raw.manifest.update_state;
    if (Object.keys(manifest).length > 0) changes.manifest = manifest;
  }

  return changes;
};

/**
 * Narrows a raw `.unit.changed` payload. Returns `null` when the envelope is malformed; the
 * `socket` key Laravel adds is ignored. `occurred_at` is only checked to be a string here; whether
 * it can be ordered is decided by `parseOccurredAt`.
 */
export const parseUnitChangedEvent = (payload: unknown): UnitChangedEvent | null => {
  if (!isObject(payload)) return null;

  const { unit_id: unitId, occurred_at: occurredAt, changes } = payload;
  if (typeof unitId !== 'number' || typeof occurredAt !== 'string' || !isObject(changes)) return null;

  return { unit_id: unitId, occurred_at: occurredAt, changes: toUnitChanges(changes) };
};

const upsertComponents = (
  current: UnitSoftwareComponentState[],
  changed: UnitSoftwareComponentState[],
): UnitSoftwareComponentState[] => {
  const next = [...current];
  for (const component of changed) {
    const index = next.findIndex((item) => item.name === component.name);
    if (index === -1) {
      next.push(component);
    } else {
      next[index] = component;
    }
  }
  return next;
};

/**
 * Merges a delta into a cached unit: scalars replace, `software_versions` and `manifest`
 * shallow-merge (a `null` manifest counts as `{}`), and `software_components` are upserted by
 * `name`, replacing the matching item in place and appending a new one.
 */
export const mergeUnitChanges = (unit: Unit, changes: UnitChanges): Unit => {
  const { software_versions: versions, software_components: components, manifest, ...scalars } = changes;
  const merged: Unit = { ...unit, ...scalars };

  if (versions) {
    merged.software_versions = { ...unit.software_versions, ...versions };
  }

  if (components) {
    merged.software_components = upsertComponents(unit.software_components ?? [], components);
  }

  if (manifest) {
    merged.manifest = unit.manifest
      ? { ...unit.manifest, ...manifest }
      : // Merging into `{}` yields a complete UnitManifest in practice: when a unit's manifest is
        // first created, the API sends every field (UnitVersionUpdateListener::changedManifestFields
        // in toverzicht-api). A field it ever left out would read as `undefined`, which every
        // consumer already renders the same way as `null`.
        ({ ...manifest } as UnitManifest);
  }

  return merged;
};

const ONLINE_KEYS: ReadonlyArray<keyof UnitOnlineChanges> = [
  'online_status',
  'is_online',
  'last_online',
  'is_mqtt_connected',
];

const MANIFEST_FIELDS: ReadonlyArray<keyof UnitManifest> = [...MANIFEST_STRING_FIELDS, 'update_state'];

const hasAnyKey = (changes: UnitChanges, keys: ReadonlyArray<keyof UnitChanges>): boolean =>
  keys.some((key) => changes[key] !== undefined);

const withoutOnlineGroup = ({
  online_status,
  is_online,
  last_online,
  is_mqtt_connected,
  ...rest
}: UnitChanges): UnitChanges => rest;

/**
 * The `occurred_at` watermarks of one unit.
 *
 * The online group has a single watermark: its four fields describe one coherent state, and its
 * broadcasts are sent in-process, in order. The software group has one per field, because SV
 * broadcasts are queued and two close ones can arrive out of order. A whole-group watermark would
 * then throw away an older delta that touches different fields.
 */
export interface UnitWatermarks {
  online?: OccurredAt;
  /** One per `software_versions` key. */
  softwareVersions: Map<string, OccurredAt>;
  /** One per `software_components` item, by `name`. */
  softwareComponents: Map<string, OccurredAt>;
  /** One per `manifest` field. */
  manifest: Map<keyof UnitManifest, OccurredAt>;
}

export const createUnitWatermarks = (): UnitWatermarks => ({
  softwareVersions: new Map(),
  softwareComponents: new Map(),
  manifest: new Map(),
});

/** Strictly older than an existing watermark. Equal is not stale, and no watermark never is. */
const isOlderThan = (occurredAt: OccurredAt, watermark: OccurredAt | undefined): boolean =>
  watermark !== undefined && compareOccurredAt(occurredAt, watermark) < 0;

/**
 * Removes every part of a delta whose own watermark is strictly newer than `occurredAt`. The online
 * group is all or nothing against its group watermark. Each `software_versions` key,
 * `software_components` item and `manifest` field is checked against its own. A container left
 * empty is removed. Pure: the watermarks are read, not changed.
 */
export const dropStaleChanges = (
  changes: UnitChanges,
  occurredAt: OccurredAt,
  watermarks: UnitWatermarks,
): UnitChanges => {
  const kept: UnitChanges = isOlderThan(occurredAt, watermarks.online) ? withoutOnlineGroup(changes) : { ...changes };

  if (kept.software_versions) {
    const versions = { ...kept.software_versions };
    for (const key of Object.keys(versions)) {
      if (isOlderThan(occurredAt, watermarks.softwareVersions.get(key))) delete versions[key];
    }
    if (Object.keys(versions).length > 0) {
      kept.software_versions = versions;
    } else {
      delete kept.software_versions;
    }
  }

  if (kept.software_components) {
    const components = kept.software_components.filter(
      (component) => !isOlderThan(occurredAt, watermarks.softwareComponents.get(component.name)),
    );
    if (components.length > 0) {
      kept.software_components = components;
    } else {
      delete kept.software_components;
    }
  }

  if (kept.manifest) {
    const manifest = { ...kept.manifest };
    for (const field of MANIFEST_FIELDS) {
      if (manifest[field] !== undefined && isOlderThan(occurredAt, watermarks.manifest.get(field))) {
        delete manifest[field];
      }
    }
    if (Object.keys(manifest).length > 0) {
      kept.manifest = manifest;
    } else {
      delete kept.manifest;
    }
  }

  return kept;
};

/** Moves the watermark of every part of an applied delta to `occurredAt`, and nothing else. */
export const advanceWatermarks = (watermarks: UnitWatermarks, applied: UnitChanges, occurredAt: OccurredAt): void => {
  if (hasAnyKey(applied, ONLINE_KEYS)) watermarks.online = occurredAt;

  for (const key of Object.keys(applied.software_versions ?? {})) {
    watermarks.softwareVersions.set(key, occurredAt);
  }

  for (const component of applied.software_components ?? []) {
    watermarks.softwareComponents.set(component.name, occurredAt);
  }

  for (const field of MANIFEST_FIELDS) {
    if (applied.manifest?.[field] !== undefined) watermarks.manifest.set(field, occurredAt);
  }
};

/** An event held in the buffer, its `occurred_at` already parsed. */
interface BufferedEvent {
  event: UnitChangedEvent;
  occurredAt: OccurredAt;
}

type ReverbEcho = Echo<'reverb'>;
type UnitStatusChannel = ReturnType<ReverbEcho['private']>;

/** The shared Echo singleton `setupEcho` stores on `window`. */
const getEcho = (): ReverbEcho | undefined => (window as Window & { Echo?: ReverbEcho }).Echo;

/**
 * Keeps the react-admin getOne cache entry of one unit live while it is mounted.
 *
 * Mount it once per unit page, at Show level (UnitActions), so it lives on every tab:
 * `useUnitLiveUpdates(record?.id)`. An undefined id or a missing localStorage token subscribes
 * to nothing, and the page keeps working on plain fetches.
 *
 * - Subscribes to `unit-status.{id}` and listens for `.unit.changed` immediately.
 * - Every `subscribed` confirmation (first connect and every reconnect) triggers exactly one
 *   refetch of the getOne key.
 * - The gate is closed while that resync is in flight, and also while ANY refetch of the key is in
 *   flight (`fetchStatus !== 'idle'`): window focus, reconnect, `useRefresh()`. react-admin's own
 *   QueryClient refetches on all three, and a delta merged during such a refetch would be
 *   overwritten by its older snapshot. The fetch status is read synchronously for each event.
 * - While the gate is closed, events are buffered in arrival order instead of applied. Starting a
 *   resync clears the buffer: whatever arrived before the refetch started was saved on the server
 *   before it, so the refetched record already contains it. A refetch the hook did not start never
 *   clears it.
 * - The buffer is replayed through the normal path (watermarks, then merge) once the gate opens,
 *   whether the refetch succeeded or failed. Three things trigger the replay: our current resync
 *   settling; a query-cache notification that the query is idle again; and a live event arriving,
 *   so older buffered events go first. All three go through one `replayBuffer`, which drains the
 *   buffer exactly once and re-checks the gate. A resync overtaken by a newer one neither replays
 *   nor clears.
 * - Watermarks (see `UnitWatermarks`): one for the whole online group, and one per software field
 *   (`software_versions` key, `software_components` name, `manifest` field). A part strictly older
 *   than its watermark is dropped, an equal one is applied, and only applied parts advance their
 *   watermark. Replayed events go through the same filter. Watermarks survive resyncs and start
 *   empty for every unit.
 * - Events for another unit and events with an unparseable `occurred_at` are ignored and never
 *   buffered. An event applied while the cache entry does not exist is ignored.
 * - Unmounting or switching units removes this hook's listener, leaves the old unit's channel,
 *   unsubscribes from the query cache and discards the buffer.
 */
export const useUnitLiveUpdates = (unitId: Identifier | null | undefined): void => {
  const queryClient = useQueryClient();
  const unitKey = unitId == null ? null : String(unitId);

  useEffect(() => {
    if (unitKey === null) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const channelName = unitStatusChannelName(unitKey);
    // react-admin stringifies the id in its getOne key (ra-core useGetOne); UnitShow passes no meta.
    const queryKey = ['units', 'getOne', { id: unitKey }];
    const queryHash = hashKey(queryKey);

    let cancelled = false;
    let channel: UnitStatusChannel | null = null;
    // Closed until the first subscription is confirmed and its resync has landed, and again from
    // every later `subscribed` until that resync settles.
    let awaitingResync = true;
    // Makes a stale resync (overtaken by a newer `subscribed`) unable to replay the buffer, clear
    // it, or reopen the gate.
    let syncGeneration = 0;
    // Events received while the gate is closed, in arrival order. Local to this effect run, and
    // emptied on cleanup, so it never outlives the subscription it belongs to.
    let buffered: BufferedEvent[] = [];
    const watermarks = createUnitWatermarks();

    // Any refetch of the key in flight, ours or not. Read from the query state at the moment each
    // event arrives, because events come from the socket, not from a cache notification, so no
    // notification is there to say whether a fetch is running.
    const isQueryFetching = (): boolean => {
      const fetchStatus = queryClient.getQueryState(queryKey)?.fetchStatus;
      return fetchStatus !== undefined && fetchStatus !== 'idle';
    };

    const isGateClosed = (): boolean => awaitingResync || isQueryFetching();

    // The one path every event takes, live or replayed: watermarks, then merge.
    const applyEvent = ({ event, occurredAt }: BufferedEvent) => {
      if (queryClient.getQueryData<Unit>(queryKey) === undefined) return;

      const changes = dropStaleChanges(event.changes, occurredAt, watermarks);
      if (Object.keys(changes).length === 0) return;

      queryClient.setQueryData<Unit>(queryKey, (current) => (current ? mergeUnitChanges(current, changes) : current));
      advanceWatermarks(watermarks, changes, occurredAt);
    };

    // Every caller runs outside render (a socket callback, a settled refetch, a cache
    // notification). A throw is logged rather than left uncaught, and the next event still goes
    // through.
    const applyEventSafely = (buffer: BufferedEvent) => {
      try {
        applyEvent(buffer);
      } catch (error) {
        console.error('Unit live updates: could not apply a unit.changed event', error);
      }
    };

    // Drains the buffer exactly once, in arrival order, and only while the gate is open. The
    // buffer is detached before the replay, so the cache notification each merge triggers
    // re-enters here and finds nothing to do.
    const replayBuffer = () => {
      if (cancelled || buffered.length === 0 || isGateClosed()) return;

      const replay = buffered;
      buffered = [];
      replay.forEach(applyEventSafely);
    };

    const handleUnitChanged = (payload: unknown) => {
      if (cancelled) return;

      // Events that cannot be applied are filtered out before buffering, so they never wait.
      const event = parseUnitChangedEvent(payload);
      if (!event || String(event.unit_id) !== unitKey) return;

      const occurredAt = parseOccurredAt(event.occurred_at);
      if (!occurredAt) return;

      if (isGateClosed()) {
        buffered.push({ event, occurredAt });
        return;
      }

      // Normally the buffer is already empty here, drained by the cache notification below. It
      // only matters when that never happened, e.g. the query was removed from the cache: older
      // events still go first.
      replayBuffer();
      applyEventSafely({ event, occurredAt });
    };

    // Replays once a refetch the hook did not start (window focus, reconnect, useRefresh) leaves
    // the query idle again. query-core notifies the cache synchronously from the query's dispatch,
    // after the reducer has already set `fetchStatus: 'idle'` and the new data, so the fetch's
    // final update replays the buffer right there, directly on top of the refetched record.
    // replayBuffer still decides from the live query state.
    const unsubscribeFromQueryCache = queryClient.getQueryCache().subscribe((cacheEvent) => {
      if (cacheEvent.type !== 'updated' || cacheEvent.query.queryHash !== queryHash) return;
      replayBuffer();
    });

    const resync = async () => {
      const generation = ++syncGeneration;
      awaitingResync = true;
      // Whatever arrived before this refetch starts was saved on the server before it, so the
      // refetch already contains it.
      buffered = [];

      try {
        await queryClient.refetchQueries({ queryKey, exact: true });
      } catch (error) {
        if (!cancelled) console.error('Unit live updates: resync refetch failed', error);
      }

      // An overtaken resync leaves the buffer and the gate to the newer one: no replay, no clear.
      if (cancelled || generation !== syncGeneration) return;

      // Open our half of the gate and replay what arrived meanwhile, on top of the refetched
      // record. After a failed refetch that is the record already cached, and deltas on top of it
      // still beat dropping them. If another refetch is in flight by now, the buffer waits for
      // the cache notification that the query is idle again.
      awaitingResync = false;
      replayBuffer();
    };

    const subscribe = async () => {
      try {
        await setupEcho(token);
        if (cancelled) return;

        const echo = getEcho();
        if (!echo) throw new Error('Echo is not initialised');

        const unitChannel = echo.private(channelName);
        channel = unitChannel;

        // Listen before the subscription is confirmed, so nothing broadcast after it is missed.
        unitChannel.listen(UNIT_CHANGED_EVENT, handleUnitChanged);

        // Pusher re-fires subscription_succeeded after every resubscribe, so this also covers
        // reconnects.
        unitChannel.subscribed(() => {
          if (cancelled) return;
          void resync();
        });

        unitChannel.error((error: unknown) => {
          if (cancelled) return;
          console.error('Unit live updates: channel subscription failed', error);
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Unit live updates: could not set up the broadcast connection', error);
      }
    };

    void subscribe();

    return () => {
      cancelled = true;
      buffered = [];
      unsubscribeFromQueryCache();
      if (channel) {
        // Removes only this hook's handler, not every `.unit.changed` listener on the channel.
        channel.stopListening(UNIT_CHANGED_EVENT, handleUnitChanged);
        getEcho()?.leave(channelName);
        channel = null;
      }
    };
  }, [unitKey, queryClient]);
};
